import React from 'react';
import { Logo, Button } from '../ui';
import { ClipboardListIcon, Users2Icon, BarChart4Icon, SparklesIcon, CheckIcon, CheckCircle2Icon, PrinterIcon, ArrowLeftIcon, HotelIcon, MapIcon, BriefcaseIcon, UsersIcon, MicIcon, Wand2Icon, LayoutDashboardIcon, GraduationCapIcon, HeartPulseIcon, NetworkIcon, MessageSquareQuoteIcon, PieChartIcon } from 'lucide-react';
export function SalesSheet() {
  const handlePrint = () => window.print();
  return <div className="min-h-screen bg-slate-100 py-10 print:bg-white print:py-0" data-id="element-6588">
      {/* Toolbar — hidden in print */}
      <div className="max-w-[850px] mx-auto px-6 mb-6 flex items-center justify-between print:hidden" data-id="element-6589">
        <a href="/" className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-brand-navy transition-colors" data-id="element-6590">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-6591" /> Back to site
        </a>
        <Button variant="primary" size="sm" onClick={handlePrint} data-id="element-6592">
          <PrinterIcon className="w-4 h-4 mr-2" data-id="element-6593" /> Download / Print PDF
        </Button>
      </div>

      {/* Sheet — letter sized */}
      <article className="bg-white max-w-[850px] mx-auto shadow-2xl print:shadow-none rounded-lg print:rounded-none overflow-hidden" style={{
      minHeight: '1100px'
    }} data-id="element-6594">
        {/* Header band */}
        <header className="bg-brand-navy text-white px-10 py-8 relative overflow-hidden" data-id="element-6595">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-brand-teal via-brand-coral to-brand-teal" data-id="element-6596"></div>
          <div className="absolute -right-20 -top-20 w-64 h-64 bg-brand-teal/10 rounded-full blur-3xl pointer-events-none" data-id="element-6597"></div>

          <div className="relative flex items-start justify-between" data-id="element-6598">
            <div data-id="element-6599">
              <div className="font-bold text-3xl tracking-tight flex items-start mb-2" data-id="element-6600">
                <span className="text-white" data-id="element-6601">Offsite</span>
                <span className="text-brand-coral text-xl leading-none font-black relative -top-0.5" data-id="element-6602">
                  +
                </span>
              </div>
              <p className="text-slate-300 text-sm font-medium" data-id="element-6603">
                The engagement layer for meetings, retreats, and group stays.
              </p>
            </div>
            <div className="text-right" data-id="element-6604">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1" data-id="element-6605">
                Sales Sheet
              </div>
              <div className="text-xs text-slate-300 font-mono" data-id="element-6606">
                v1.0 · 2026
              </div>
            </div>
          </div>
        </header>

        {/* Body */}
        <div className="px-10 py-8 space-y-8" data-id="element-6607">
          {/* Headline */}
          <section data-id="element-6608">
            <h1 className="text-3xl font-bold text-brand-navy leading-tight mb-3 text-balance" data-id="element-6609">
              Turn any offsite into a structured, measurable experience.
            </h1>
            <p className="text-sm text-slate-600 leading-relaxed" data-id="element-6610">
              Offsite+ helps planners and hospitality partners design, run, and
              measure better offsites with AI-generated planning, attendee
              intake, learning pathways, group matching, wellness preferences,
              and client-ready reporting.
            </p>
          </section>

          {/* Problem / Solution stripe */}
          <section className="grid grid-cols-2 gap-4" data-id="element-6611">
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-5" data-id="element-6612">
              <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-brand-coral mb-2" data-id="element-6613">
                The Problem
              </div>
              <p className="text-xs text-slate-700 leading-relaxed" data-id="element-6614">
                Most offsites are expensive but hard to measure. Attendees
                arrive unprepared, networking is random, preferences are missed,
                and the client leaves without a clear view of what worked.
              </p>
            </div>
            <div className="bg-brand-navy text-white rounded-xl p-5" data-id="element-6615">
              <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-brand-teal mb-2" data-id="element-6616">
                The Solution
              </div>
              <p className="text-xs text-slate-200 leading-relaxed" data-id="element-6617">
                Offsite+ adds the missing experience layer before, during, and
                after the event — so planners and hospitality partners deliver
                more value with less chaos.
              </p>
            </div>
          </section>

          {/* Before / During / After */}
          <section data-id="element-6618">
            <h2 className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500 mb-4" data-id="element-6619">
              The Experience Layer
            </h2>
            <div className="grid grid-cols-3 gap-3" data-id="element-6620">
              {[{
              label: 'Before',
              icon: <ClipboardListIcon className="w-4 h-4" data-id="element-6621" />,
              color: 'border-brand-teal bg-brand-teal/5 text-brand-teal',
              items: ['Attendee intake', 'Goals & preferences', 'Introductions', 'AI blueprint']
            }, {
              label: 'During',
              icon: <Users2Icon className="w-4 h-4" data-id="element-6622" />,
              color: 'border-brand-coral bg-brand-coral/5 text-brand-coral',
              items: ['Event hub', 'Agenda & prompts', 'Group matching', 'Activity selection']
            }, {
              label: 'After',
              icon: <BarChart4Icon className="w-4 h-4" data-id="element-6623" />,
              color: 'border-brand-navy bg-brand-navy/5 text-brand-navy',
              items: ['Surveys', 'Commitments', 'Analytics', 'Client report']
            }].map(stage => <div key={stage.label} className={`rounded-xl border-l-4 ${stage.color} bg-white border-t border-r border-b border-slate-200 p-4`} data-id="element-6624">
                  <div className={`flex items-center gap-2 mb-3 ${stage.color.split(' ')[2]}`} data-id="element-6625">
                    {stage.icon}
                    <span className="text-sm font-bold" data-id="element-6626">{stage.label}</span>
                  </div>
                  <ul className="space-y-1.5" data-id="element-6627">
                    {stage.items.map(item => <li key={item} className="text-xs text-slate-600 flex items-center gap-1.5" data-id="element-6628">
                        <div className="w-1 h-1 rounded-full bg-slate-400" data-id="element-6629"></div>
                        {item}
                      </li>)}
                  </ul>
                </div>)}
            </div>
          </section>

          {/* Key features */}
          <section data-id="element-6630">
            <h2 className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500 mb-4" data-id="element-6631">
              Key Features
            </h2>
            <div className="grid grid-cols-4 gap-2" data-id="element-6632">
              {[{
              icon: <Wand2Icon className="w-3.5 h-3.5" data-id="element-6633" />,
              label: 'Free AI Designer'
            }, {
              icon: <ClipboardListIcon className="w-3.5 h-3.5" data-id="element-6634" />,
              label: 'Branded Intake'
            }, {
              icon: <LayoutDashboardIcon className="w-3.5 h-3.5" data-id="element-6635" />,
              label: 'Event Hub'
            }, {
              icon: <GraduationCapIcon className="w-3.5 h-3.5" data-id="element-6636" />,
              label: 'Learning Pathways'
            }, {
              icon: <HeartPulseIcon className="w-3.5 h-3.5" data-id="element-6637" />,
              label: 'Wellness & Activities'
            }, {
              icon: <NetworkIcon className="w-3.5 h-3.5" data-id="element-6638" />,
              label: 'Group Matching'
            }, {
              icon: <MessageSquareQuoteIcon className="w-3.5 h-3.5" data-id="element-6639" />,
              label: 'Reflection Prompts'
            }, {
              icon: <PieChartIcon className="w-3.5 h-3.5" data-id="element-6640" />,
              label: 'Post-Event Report'
            }].map(f => <div key={f.label} className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 flex items-center gap-2" data-id="element-6641">
                  <span className="text-brand-teal shrink-0" data-id="element-6642">{f.icon}</span>
                  <span className="text-[11px] font-semibold text-brand-navy leading-tight" data-id="element-6643">
                    {f.label}
                  </span>
                </div>)}
            </div>
          </section>

          {/* Intelligence Engine callout */}
          <section className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-xl p-5 flex items-start gap-4" data-id="element-6644">
            <div className="bg-brand-coral/10 p-2.5 rounded-lg shrink-0" data-id="element-6645">
              <SparklesIcon className="w-5 h-5 text-brand-coral" data-id="element-6646" />
            </div>
            <div data-id="element-6647">
              <h3 className="text-sm font-bold text-brand-navy mb-1" data-id="element-6648">
                Offsite+ Intelligence Engine
              </h3>
              <p className="text-xs text-slate-600 leading-relaxed" data-id="element-6649">
                AI-powered learning pathways built from proven leadership,
                wellness, and executive-education frameworks.
              </p>
            </div>
          </section>

          {/* Pilot offer — hero block */}
          <section className="border-2 border-brand-coral rounded-xl overflow-hidden relative" data-id="element-6650">
            <div className="absolute top-3 right-3 bg-brand-coral text-white text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider" data-id="element-6651">
              Pilot Offer
            </div>
            <div className="bg-brand-coral/5 px-5 pt-5 pb-4" data-id="element-6652">
              <div className="flex items-baseline gap-3 mb-1" data-id="element-6653">
                <h3 className="text-lg font-bold text-brand-navy" data-id="element-6654">
                  Founding Partner Pilot
                </h3>
              </div>
              <div className="flex items-baseline gap-2" data-id="element-6655">
                <span className="text-2xl font-black text-brand-navy" data-id="element-6656">
                  $1,500–$3,500
                </span>
                <span className="text-xs text-slate-500 font-medium" data-id="element-6657">
                  per event
                </span>
              </div>
            </div>
            <div className="px-5 py-4 bg-white" data-id="element-6658">
              <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 mb-2" data-id="element-6659">
                Includes
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5" data-id="element-6660">
                {['AI offsite blueprint', 'Branded intake form', 'Event hub setup', 'Curated learning pathway', 'Group matching structure', 'Survey flow', 'Post-event report'].map(item => <div key={item} className="flex items-center gap-2 text-xs text-slate-700" data-id="element-6661">
                    <CheckCircle2Icon className="w-3.5 h-3.5 text-brand-teal shrink-0" data-id="element-6662" />
                    {item}
                  </div>)}
              </div>
            </div>
          </section>

          {/* Who it's for */}
          <section data-id="element-6663">
            <h2 className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500 mb-3" data-id="element-6664">
              Who It's For
            </h2>
            <div className="grid grid-cols-5 gap-2" data-id="element-6665">
              {[{
              icon: <HotelIcon className="w-4 h-4" data-id="element-6666" />,
              label: 'Hotels & Resorts'
            }, {
              icon: <MapIcon className="w-4 h-4" data-id="element-6667" />,
              label: 'DMCs'
            }, {
              icon: <BriefcaseIcon className="w-4 h-4" data-id="element-6668" />,
              label: 'MICE Planners'
            }, {
              icon: <UsersIcon className="w-4 h-4" data-id="element-6669" />,
              label: 'Corporate HR'
            }, {
              icon: <MicIcon className="w-4 h-4" data-id="element-6670" />,
              label: 'Retreat Facilitators'
            }].map(a => <div key={a.label} className="border border-slate-200 rounded-lg p-3 flex flex-col items-center text-center gap-1.5 bg-white" data-id="element-6671">
                  <div className="text-brand-navy" data-id="element-6672">{a.icon}</div>
                  <div className="text-[10px] font-semibold text-slate-700 leading-tight" data-id="element-6673">
                    {a.label}
                  </div>
                </div>)}
            </div>
          </section>

          {/* CTAs */}
          <section className="bg-brand-navy rounded-xl px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-4 relative overflow-hidden" data-id="element-6674">
            <div className="absolute -right-10 -bottom-10 w-40 h-40 bg-brand-coral/20 rounded-full blur-3xl pointer-events-none" data-id="element-6675"></div>
            <div className="relative" data-id="element-6676">
              <div className="text-white font-bold text-base mb-0.5" data-id="element-6677">
                Ready to design a better offsite?
              </div>
              <div className="text-slate-300 text-xs" data-id="element-6678">
                Start free, or talk to our pilot team.
              </div>
            </div>
            <div className="relative flex gap-2 shrink-0" data-id="element-6679">
              <button className="bg-brand-coral hover:bg-orange-500 text-white text-xs font-semibold px-4 py-2.5 rounded-full transition-colors" data-id="element-6680">
                Design Your Free Offsite
              </button>
              <button className="border border-white/30 hover:bg-white/10 text-white text-xs font-semibold px-4 py-2.5 rounded-full transition-colors" data-id="element-6681">
                Book a Pilot Demo
              </button>
            </div>
          </section>
        </div>

        {/* Footer */}
        <footer className="border-t border-slate-200 px-10 py-5 flex items-center justify-between bg-slate-50" data-id="element-6682">
          <p className="text-[11px] text-slate-600 italic max-w-md" data-id="element-6683">
            Offsite+ helps group experiences become easier to sell, easier to
            run, and easier to justify.
          </p>
          <div className="text-[10px] text-slate-400 font-mono" data-id="element-6684">
            offsiteplus.com
          </div>
        </footer>
      </article>

      {/* Print styles */}
      <style data-id="element-6685">{`
        @media print {
          @page { size: letter; margin: 0; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>;
}